export interface TemplatePreferences {
  recentTemplateIds: string[];
  defaultTemplateId: string | null;
}

const STORAGE_KEY = "templatePreferences";
const MAX_HISTORY = 5;

const defaultPreferences: TemplatePreferences = {
  recentTemplateIds: [],
  defaultTemplateId: null,
};

const sanitizePreferences = (
  preferences: Partial<TemplatePreferences> | null | undefined,
): TemplatePreferences => {
  const recentTemplateIds = Array.from(
    new Set((preferences?.recentTemplateIds || []).filter(Boolean)),
  ).slice(0, MAX_HISTORY);

  return {
    recentTemplateIds,
    defaultTemplateId: preferences?.defaultTemplateId ?? null,
  };
};

function readPreferences(): TemplatePreferences {
  if (typeof window === "undefined") return { ...defaultPreferences };

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultPreferences };

    const parsed = JSON.parse(raw) as Partial<TemplatePreferences>;
    return sanitizePreferences(parsed);
  } catch (error) {
    console.error("Failed to read template preferences", error);
    return { ...defaultPreferences };
  }
}

function writePreferences(preferences: TemplatePreferences) {
  if (typeof window === "undefined") return;

  try {
    const sanitized = sanitizePreferences(preferences);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
  } catch (error) {
    console.error("Failed to write template preferences", error);
  }
}

export function getRecentTemplateIds(): string[] {
  return readPreferences().recentTemplateIds;
}

export function getDefaultTemplateId(): string | null {
  return readPreferences().defaultTemplateId ?? null;
}

export function addRecentTemplate(templateId: string): string[] {
  const preferences = readPreferences();
  const uniqueHistory = [templateId, ...preferences.recentTemplateIds.filter((id) => id !== templateId)];

  const recentTemplateIds = uniqueHistory.slice(0, MAX_HISTORY);
  writePreferences({ ...preferences, recentTemplateIds });
  return recentTemplateIds;
}

export function setDefaultTemplateId(templateId: string | null): string | null {
  const preferences = readPreferences();
  writePreferences({ ...preferences, defaultTemplateId: templateId });
  return templateId;
}

export function clearRecentTemplates() {
  const preferences = readPreferences();
  writePreferences({ ...preferences, recentTemplateIds: [] });
}

export function clearDefaultTemplate() {
  const preferences = readPreferences();
  writePreferences({ ...preferences, defaultTemplateId: null });
}
